export const designer = {
  name: "Shakti Rai",
  role: "Lead UI/UX Designer",
  location: "Mumbai · Remote",
  yearsExperience: 5,
  headline: [
    { text: "Designing products that work for ", tone: "primary" },
    { text: "people, systems, and business", tone: "italic-accent" },
    { text: ".", tone: "primary" },
  ],
  intro:
    "I help growth-stage products stop leaking revenue at the interface layer, pairing measurable outcomes with the messy reality of what users actually need.",
  portrait: "/shakti-portrait.webp",
  resume: "/shakti-rai-resume.pdf",
  email: "raishakti668@gmail.com",
  socials: [
    { label: "LinkedIn", href: "https://www.linkedin.com/in/shakti--rai/" },
    { label: "Email", href: "mailto:raishakti668@gmail.com" },
  ],
};

export const marqueeWords = [
  "Product Strategy",
  "Product Analytics",
  "Experimentation",
  "Funnel Optimisation",
  "CRM & Internal Tools",
  "Data-Informed UX",
  "Automation",
  "Growth Systems",
];

export const projects = [
  {
    id: "uniacco-pdp",
    title: "UniAcco PDP, rebuilt",
    category: "PropTech · Web Design · B2C",
    summary:
      "Students were making a high-consideration housing decision on pages that buried key information, fragmented trust signals, and offered unclear next steps. I revamped how students interacted with the page: restructuring information, surfacing decision-critical content, and creating a clearer path from evaluation to enquiry.",
    role: "Lead UI/UX designer",
    year: "2026",
    color: "#F7F7FA",
    accent: "#0E1A2B",
    image: "/case-studies/uniacco/pdp-hero.webp",
    thumbnail: { kind: "desktop", src: "/case-studies/uniacco/pdp-hero.webp" },
    study: {
      timeframe: "10 weeks · Launched May 2026",
      team: "Design lead · 1 PM · 3 engineers · SEO consultant",
      platform: "Responsive web · Mobile-first · 390 → 1440px",
      overview:
        "UniAcco is a cross-border platform helping international students find and book accommodation across the UK, Ireland, Australia and beyond. Unlike a marketing site, UniAcco's biggest search surface isn't the homepage, it's the property detail page, a single template multiplied across thousands of listings. When that template underperforms, it underperforms at scale. That's what made it the highest-leverage design project in the company: fix the template once, every listing gets better.",
      problem: [
        "Close to zero organic traffic: property pages contributed a rounding-error share of sessions while UHomes ran on 64% organic. UniAcco pulled ~872K visits vs Amber's 2.64M.",
        "Worst engagement in the competitive set: 48% bounce (peers 39–45%), 1m52s visit duration (peers up to 3m08s), 2.72 pages per visit (Amber: 3.68).",
        "14 collapsed policy accordions occupied more page real estate than photos, reviews and location combined: a 39-item facility checklist, dense room-tenancy matrix, unformatted legalese.",
        "Three conversion surfaces fighting on one screen: price card, full lead form, and 'Book Now' buttons on every table row, with no hierarchy between self-serve and assisted paths.",
        "A link farm above the footer: hundreds of undifferentiated text links diluted page authority instead of distributing it, with near-zero user clicks.",
      ],
      problemGroups: [
        {
          heading: "The page couldn't help anyone decide",
          items: [
            "A duplicated room matrix: the same price repeated across three duration rows, each with its own Book Now button. Students were comparing rows, not rooms.",
            "Trust signals buried below the fold: rating, verification and reviews sat under a view-counter and an SEO copy block, invisible in the five seconds a student decides.",
            "14 collapsed policy accordions occupied more page real estate than photos, reviews and location combined: a 39-item facility checklist, dense room-tenancy matrix, unformatted legalese.",
            "Three conversion surfaces fighting on one screen: price card, full lead form, and 'Book Now' buttons on every table row, with no hierarchy between self-serve and assisted paths.",
          ],
        },
        {
          heading: "And almost no one could find it",
          items: [
            "Close to zero organic traffic: property pages contributed a rounding-error share of sessions while UHomes ran on 64% organic. UniAcco pulled ~872K visits vs Amber's 2.64M.",
            "A link farm above the footer: hundreds of undifferentiated text links diluted page authority instead of distributing it, with near-zero user clicks.",
          ],
          emphasis:
            "Organic was a rounding error, which means nearly everyone on this page arrived from paid or direct. The findability problem capped the ceiling. The decision problem was leaking traffic we'd already paid for.",
        },
      ],
      impactTop: [
        { value: "−39.09%", label: "Bounce rate", evidence: "measured" },
        { value: "+18.23%", label: "Traffic → Lead CVR", evidence: "measured" },
        { value: "+17.38%", label: "Caterable leads", evidence: "measured" },
        { value: "+19.28%", label: "Views per session", evidence: "measured" },
      ],
      impactProvenance:
        "These are conversion outcomes. The structural SEO work is unproven: the page launched in May, organic compounds over quarters, and I don't have post-launch ranking data. I'd call that half sound in principle and unmeasured.",
      redesignShowcase: null,
      decisionsShowBeforeAfter: true,
      approach: [
        {
          title: "One page, one conversation",
          body: "A single conversion system with two honest intents, self-serve 'Reserve now' and assisted 'Take expert help', replaces three competing widgets. Sticky CTA on mobile echoes the same hierarchy.",
        },
        {
          title: "Show, don't list",
          body: "Every data dump becomes a designed object. 39-item checklists become icon summaries grouped by intent. Table rows become room cards with photos. Text bullets become mapped, categorised places with walk times.",
        },
        {
          title: "Structure is the SEO strategy",
          body: "Page section order mirrors the student's decision journey: see it, trust it, price it, understand terms, act. That same spine gives crawlers a clean semantic outline: one idea per section, one heading per idea.",
        },
        {
          title: "Every scroll earns the next",
          body: "Each section ends with a reason to continue. Engagement time isn't vanity, it's a ranking signal the page is deliberately designed to earn.",
        },
      ],
      decisionsTitle: "A few of the decisions that rebuilt the page.",
      decisions: [
        {
          id: "D—01",
          title: "Immersive gallery grid replaces the buried photo strip",
          section: "Hero section",
          after:
            "A 1+4 grid surfaces five real photos on load, with Virtual Tour promoted onto the hero image and \"Show All Images\" replacing the old \"1/54\" counter.",
          why:
            "The old hero buried the gallery behind one photo and two placeholder thumbnails, leaving most of the 54 images unseen. Heatmaps showed people clicking those thumbnails anyway, the demand to browse was already there, just unsurfaced.",
          signal:
            "Gallery interaction rate · virtual-tour opens · first-impression dwell.",
          beforeImage: "/case-studies/uniacco/d01-gallery-before.png",
          afterImage: "/case-studies/uniacco/d01-gallery-after.png",
        },
        {
          id: "D—02",
          title: "A trust stack above the fold, rating, verification, honest badges",
          section: "Hero section",
          after:
            "Rating and trust now sit beside the property name: ★4.8 · 23 ratings · Verified, where a plain view-count badge used to be. The address shortens into a clickable line, SEO copy moves down out of the hero, and amenity icons are replaced by anxiety-specific tags (Bills Included, No University No Pay, No Visa No Pay).",
          why:
            "The first five seconds decide trust for a student booking sight-unseen abroad. Moving credibility signals into the space that used to hold a view counter and full address puts proof where eyes land first, without deleting the SEO text, just deprioritising it.",
          signal:
            "Bounce rate on organic entries: the first five seconds decide whether a search visitor stays. Review count near the title also strengthens snippet CTR.",
          measured:
            "Bounce fell 14.5% → 9.3% within two months of launch; avg session hit an all-time high of 3.61 minutes.",
          beforeImage: "/case-studies/uniacco/d04-amenities-before.png",
          afterImage: "/case-studies/uniacco/d04-amenities-after.png",
        },
        {
          id: "D—03",
          title: "The room matrix becomes room cards a student can actually compare",
          section: "Room Cards",
          after:
            "One card per room: photo, name, a scarcity flag (\"2 Rooms Left\"), and the four specs students weigh, up front. Per-duration rows collapse into a single \"Booking Period\" dropdown, with price and savings sitting above two clear actions, Take Expert Help and Reserve Now.",
          why:
            "The old matrix repeated the same price across three duration rows, each with its own Book Now button, comparing rows, not rooms. One dropdown and two honest CTAs turn a spreadsheet into a single decision.",
          signal:
            "Room-section engagement and scroll continuation past it: the exact point where the old scrollmap fell off a cliff.",
          measured:
            "Room Options became the single largest lead source on the page: 41.7% of mobile leads and 40.1% of desktop leads originate inside the room cards.",
          beforeImage: "/case-studies/uniacco/d03-rooms-before.png",
          afterImage: "/case-studies/uniacco/d03-rooms-after.png",
        },
        {
          id: "D—04",
          title: "Offers and guarantees: paragraph list → tagged deal cards + one assurance strip",
          section: "Offers & guarantee",
          after:
            "Four cashback offers and two assurance items, once stacked as paragraphs with fine print, become tagged \"Student Deals\" cards (Exclusive, Last Minute, Budget Deal), each headlined with one number, \"Earn up to $1000,\" under a single \"Save up to $1600\" badge. Assurance content collapses into a small tag strip (24x7 Personal Assistance, Price Match Guarantee) instead of its own paragraph block.",
          why:
            "Four offers buried in fine print gave no sense of value or relevance at a glance. One number per labeled card makes value scannable; guarantees move from an essay to two tags without losing the reassurance.",
          signal:
            "Deals-module click-through; assurance-strip visibility on scroll.",
          beforeImage: "/case-studies/uniacco/d08-offers-before.png",
          afterImage: "/case-studies/uniacco/d08-offers-after.png",
        },
        {
          id: "D—05",
          title: "Location: text bullets → a structured neighbourhood module",
          section: "Map & exploration",
          after:
            "\"Explore Nearby Places\" now groups results under labeled categories: Entertainment, Bar, Commute, Supermarket, each with a named place and walk time, replacing one flat list mixed with generic marketing bullets.",
          why:
            "The old module read like copywriting with a few walk-times attached, with no way to tell a supermarket from a train stop at a glance. Categorising by the question a student is actually asking turns a list into a decision aid.",
          signal:
            "Long-tail \"near [university/landmark]\" rankings, plus map interactions.",
          beforeImage: "/case-studies/uniacco/d05-location-before.png",
          afterImage: "/case-studies/uniacco/d05-location-after.png",
        },
        {
          id: "D—06",
          title: "Reviews rebuilt as 'Real Student Experiences'",
          section: "Reviews",
          after:
            "The section now opens with a \"4.0 Excellent\" score and an AI-written summary card, then category ratings and a photo strip, before named, verified student reviews carrying category tags, replacing the old bar-chart-plus-comment-list format.",
          why:
            "The old reviews were functional but read as a wall of text. A scannable summary and score give skimmers the verdict instantly, while verified reviews underneath keep the evidence intact for anyone who wants it.",
          signal:
            "Dwell time in the trust zone and enquiry rate, plus unique review text that differentiates each listing.",
          beforeImage: "/case-studies/uniacco/d06-reviews-before.png",
          afterImage: "/case-studies/uniacco/d06-reviews-after.png",
        },
      ],
      reflection:
        "SEO is an information-architecture problem before it's a keyword problem. We never 'optimised copy'; we gave the page a spine that matched how students decide, and rankings followed the structure. Design owned the outcome, not just the surface.",
      learnings: [
        "Deleting is a design decision. The price-trend chart, the urgency counter, eleven policy accordions, the link farm: some of the highest-impact moves were removals. Attention data made those cuts defensible.",
        "Design the measurement with the page. Defining per-section engagement events with the PM before build meant that at launch we could attribute movement to specific decisions, turning the next roadmap debate into a data conversation, not an opinion contest.",
        "Time on page is a means, not an end. Session duration dipped slightly YoY while conversion jumped: students stopped spending time searching and started spending it deciding. Treated as a goal, that's a regression; treated as a signal, it's the redesign working.",
      ],
      gallery: [],
    },
  },
  {
    id: "uniacco-copilot",
    title: "Donna — AI Sales Co-Pilot",
    category: "AI UX · B2B SaaS · CRM",
    summary:
      "UniAcco's sales agents were losing ~35% of every workday to non-selling admin: hunting property info across Excel, Slack and WhatsApp before ever talking to a student. Donna is the AI co-pilot that gives that time back: ranking properties, drafting the message, and sending it, with the agent's hands still on the wheel. No new headcount required.",
    role: "Lead UX designer · 10 weeks · In production",
    year: "2025",
    color: "#EFF0F4",
    accent: "#E5326A",
    image: "/case-studies/donna/d02.png",
    thumbnail: { kind: "desktop", src: "/case-studies/donna/d02.png" },
    study: {
      timeframe: "10 weeks · 2025 · In production",
      team: "UX lead · 1 PM · AI/ML team · CRM engineers",
      platform: "Embedded browser workspace · One tab per lead",
      impactTop: [
        { value: "2h → 15m", label: "Shortlist time" },
        { value: "10 → 16", label: "Leads / agent / day" },
        { value: "9 / 10", label: "Would use daily", evidence: "tested" },
      ],
      overview:
        "UniAcco is a student accommodation platform running across 200+ cities. Sales agents take inbound leads, match students to properties, and close, juggling 10+ live conversations a day. The ceiling wasn't talent; it was capacity. Roughly 35% of every day disappeared into non-selling work: cross-referencing 3–5 sources per shortlist, drafting messages after the call ended, hunting policy answers mid-call. Donna is the co-pilot that closes that gap: a workspace tab per student, opened straight from the CRM, that surfaces context, ranks properties, drafts the message, and sends it. Agents keep their hands on the wheel.",
      problem: [
        "~35% of every workday lost to non-selling tasks: hunting property info, drafting messages, answering policy questions before a single lead is worked.",
        "Data lived in 3–5 places per shortlist: Excel sheets, property sites, Slack, WhatsApp, personal notes. No single source of truth.",
        "One shortlist took 30 minutes to 2 hours to build and send. At 10+ leads a day, the math ate most of the working hours.",
        "New-agent ramp took ~6 months. Senior agents held mental maps of the database that walked out when they left.",
        "Post-call CRM friction meant shortlists often went out late, or never. The next call had already started.",
      ],
      approach: [
        {
          title: "This was a capacity project, not an AI project",
          body: "Reframing changed everything about prioritisation. The goal wasn't 'ship AI'; it was to give back the ~35% of the day the current system was consuming. AI was the tool that made the maths work, not the point.",
        },
        {
          title: "Co-pilot, not chatbot",
          body: "Agents needed a parallel workspace they could hold open beside the call, not a modal that interrupts, not a chat stream that competes for reading time. Donna opens as a dedicated browser tab per student, pre-loaded with that lead's full context.",
        },
        {
          title: "Transparency is the adoption mechanism",
          body: "Agents who feel the AI is acting without them disengage. Agents who see the reasoning, why this property, why this rank, start adding to compare unprompted. Every recommendation shows its 'why' before it shows its 'yes'.",
        },
        {
          title: "Predictable beats impressive",
          body: "For compliance-sensitive answers, consistency is worth more than generative flexibility. The embedded help desk is pre-authored and keyword-matched across seven policy categories, with a manager escalation for edge cases. No hallucinations at the seam that matters most.",
        },
      ],
      decisionsEyebrow: "— The build",
      decisionsTitle: "5 decisions that shaped Donna.",
      decisionsSubhead:
        "Designed as a workspace tab that lives alongside the CRM. Each decision below shows what was rejected, what shipped, the reasoning, and the signal it was designed to move.",
      howIWorkedEyebrow: "— How I worked",
      howIWorked: [
        {
          title: "Reframing the brief from AI project to capacity project",
          body: "The original ask was 'build a sales AI'. In week one I made the case that the ceiling wasn't intelligence, it was time: ~35% of every agent day was burning on non-selling work. Once the PM adopted the frame, it rewrote our prioritisation stack. Every feature had to first answer 'how many minutes does this give back?' before it earned space in the sprint.",
        },
        {
          title: "Landing pre-authored policy answers with the AI/ML team",
          body: "For policy, I argued against LLM generation, a position the AI/ML team initially pushed back on. We ran a two-week hardening spike alongside a keyword-matched prototype; the LLM was accurate most of the time and hallucinated a cancellation clause once. Once wasn't survivable in a compliance space where a wrong booking clause can send a student into an unrecoverable dispute. We shipped consistency over cleverness, and kept the AI where it earned trust.",
        },
        {
          title: "Selling 'one browser tab per lead' to CRM engineers",
          body: "A dedicated tab per student was a non-trivial integration ask: new session state, deep-links from lead records, close hooks writing back to the CRM. I brought the engineers into two sessions of agent-shadowing so they saw the three-parallel-conversations reality before we scoped the work. The build came in on estimate; the pattern they cared about most, adoption, is the metric that tracked strongest.",
        },
      ],
      decisions: [
        {
          id: "D—01",
          title: "Donna lives as a dedicated browser tab per lead, not a chat bubble, not a modal",
          before:
            "Floating chat bubble: rejected because it forces a single lead into focus at a time and doesn't match how agents already work with three parallel students. Contextual modal: rejected because modals had to be reopened after every lead-switch and blocked the CRM behind them; the more it interrupted, the less it got used. A dedicated tab per lead mapped to muscle memory the team already had.",
          after: "One browser tab per student, opened from the lead record. Each tab title shows the student name: three open Donnas scan at a glance. 'Done' closes the tab and logs completion to the CRM.",
          why: "Agents handle 3–5 live students at once. A dedicated tab maps to muscle memory they already have; tab hygiene doubles as a lightweight workflow signal.",
          signal: "Tabs opened per day · tabs closed with 'Done' · time between open and first action.",
        },
        {
          id: "D—02",
          title: "Recommendations as scannable cards in a collapsible tray",
          before: "Full-screen results broke context entirely. A linear chat stream forced full reading while the agent was mid-conversation.",
          after: "Result cards agents can jump into (price, distance, availability, commission) with a collapsible tray so the top three stay visible while deeper detail is a click away.",
          why: "Live calls reward scanning, not reading. Cards let the eye pick the answer without pulling attention off the student.",
          signal: "Time-to-first-recommendation (target: ~50s) · cards opened per call.",
          builtImage: "/case-studies/donna/d02.png",
        },
        {
          id: "D—03",
          title: "Shortlist send is an editable preview with one-tap WhatsApp / email",
          before:
            "Auto-send: rejected in prototype testing because agents felt the AI was speaking for them; even accurate messages read as 'not mine' and lost trust the moment they went out. Copy-to-clipboard: rejected because it just moved the manual work into another tab and every tester skipped it. The agent had to stay in the loop as the author, not the reviewer.",
          after: "AI drafts the message with properties pre-filled; agent reviews inline, edits tone, adds a personal note, taps send. Message ships via WhatsApp or email without ever leaving Donna.",
          why: "AI earns trust through control, not convenience. The message has to feel like it came from the agent, because it did.",
          signal: "Edit-rate before send (guardrail: never 0%) · shortlists sent within 30 min of call end.",
          builtImage: "/case-studies/donna/d03.png",
        },
        {
          id: "D—04",
          title: "'Which is cheaper?' becomes a dynamic comparison table",
          before: "Manual multi-tab comparison: the exact pain we were solving. An AI-generated summary paragraph forced full reading mid-call.",
          after: "A side-by-side comparison table built live from properties the agent picks. Differences highlighted. Toggle between detailed and summary view.",
          why: "The most common mid-call question is a comparison. Structure the answer so it takes a glance, not a sentence.",
          signal: "Comparison invocations per call · properties added to compare unprompted (adoption tell).",
          builtImage: "/case-studies/donna/d04.png",
        },
        {
          id: "D—05",
          title: "Policy answers are pre-authored, keyword-matched, not LLM-generated",
          before:
            "Full LLM-generated answers: rejected because this is the highest-risk seam in the product; in a compliance space, a hallucinated cancellation or refund clause can push a student into an unrecoverable dispute, and generative flexibility becomes a liability, not a feature. External knowledge-base links: rejected because they broke tab flow the moment they opened a new domain. Pre-authored + keyword-matched was the only path that stayed fast and safe.",
          after: "An embedded help desk covering seven common categories, cancellations, guarantors, VAS, booking, payment, follow-up, house rules, with a manager-CTA for anything out of scope. Fast, consistent, safe.",
          why: "For policy answers, consistency > cleverness. The rare edge case escalates gracefully to a human. No dead ends, no hallucinations.",
          signal: "Out-of-scope query rate (guardrail: rising rate = expand coverage) · policy answers surfaced per week.",
          builtImage: "/case-studies/donna/d05.png",
        },
      ],
      outcomes: [
        { metric: "2hr → 15m", label: "Shortlist time: from 30min–2hr manual hunt to a Donna-drafted, agent-reviewed send" },
        { metric: "10 → 16", label: "Leads per agent per day: throughput uplift with the same team" },
        { metric: "9 / 10", label: "Agents in prototype testing said they'd use Donna daily if it shipped tomorrow", evidence: "tested" },
      ],
      reflection:
        "Donna works when agents stop thinking about it. The goal was never to ship an AI tool, it was to give a fixed-headcount sales team capacity they didn't have before. Reframing this as a capacity project, not a UX project, changed every prioritisation call after it.",
      learnings: [
        "Test assumptions, not designs. Three bets could have broken Donna: trust in AI ranks, willingness to edit drafted messages, and whether an embedded help desk could replace the KB. Prototype sessions targeted each one before a pixel of UI was polished.",
        "Live-call behaviour and post-call behaviour aren't the same design problem. A diary study alongside interviews would have caught that earlier, I'd start there next time.",
        "Define the measurement framework before the first screen. Metrics-first would have sharpened feature prioritisation from week one instead of week eight.",
      ],
      gallery: [],
    },
  },
  {
    id: "unicreds-crm",
    title: "UniCreds CRM, rethought",
    category: "FinTech · Enterprise · B2B",
    summary:
      "UniCreds' loan CRM moves one student lead through multiple lenders, roles, permissions and a ~65-day journey, and the old system had become impossible to reason about. I led its redesign as a systems problem: mapping the objects and relationships first, then designing the interface as the visible layer of that system.",
    role: "Lead product designer",
    year: "2025",
    color: "#EFF0F4",
    accent: "#E5326A",
    image: "/case-studies/unicreds/crm-thumb.svg",
    thumbnail: { kind: "desktop", src: "/case-studies/unicreds/lead-details.webp" },
    study: {
      timeframe: "14 weeks · 2025 · Shipped",
      team: "Design lead · 1 PM · Ops leads (UC + lender) · Engineering",
      platform: "Web app · Role-adaptive · Desktop-first",
      overview:
        "UniCreds routes international student loans across multiple lenders. A single lead can carry several applications, each with its own lender, status, documents, tasks, notes and communication trail, and a journey that runs ~65 days on average. The old CRM had gradually become hard to reason about: the same lead behaved differently depending on who was looking at it, but the interface didn't encode those rules. I led the end-to-end redesign as a systems problem, mapping the underlying objects and their relationships first, then designing the surface as the visible layer of that model.",
      problem: [
        "One lead ≠ one application. A single student could carry multiple loan applications with different lenders, each with its own status and timeline, and the CRM flattened all of that into a single view.",
        "Two very different sides on the same data. UC agents coordinated across lenders; lenders worked only their assigned applications. Same object, opposite responsibilities, with no permission architecture to express that difference.",
        "Status was a label, not a system. A status change often required new fields, new actions and downstream notifications, but the interface treated it as a colour on a chip.",
        "Collaboration was invisible. Notes, tasks and communication were siloed by team and quietly disappeared the moment ownership changed hands.",
        "The ~65-day journey turned every lost piece of context into hours of re-work. When operational memory lives in someone's head, it doesn't scale.",
      ],
      impactTop: [
        { value: "2 roles", label: "One CRM · role-adaptive" },
        { value: "12", label: "Application states, each a system" },
        { value: "~65 days", label: "Operational context preserved end-to-end" },
      ],
      impactProvenance:
        "A shipped enterprise CRM inside UniCreds. Success signals were adoption-shaped: lender status updates, self-created and cross-assigned tasks, not marketing-dashboard numbers. This case study is about the reasoning and craft.",
      approach: [
        {
          title: "Make responsibility visible",
          body: "Users should always know what belongs to them and what belongs to someone else. Ownership isn't a badge on a row, it's a first-class layout decision.",
        },
        {
          title: "Treat states as systems, not labels",
          body: "A status determines available information, available actions, required fields, communication and downstream events. Once encoded that way, automation sits naturally on top of the model.",
        },
        {
          title: "Design around relationships",
          body: "Lead, lender, application, document, task and person relationships matter more than individual screens. Every screen is the visible slice of a graph; the graph is the product.",
        },
        {
          title: "Preserve context",
          body: "Timeline, notes and notifications ensure that when a lead changes hands, the work that came before it doesn't vanish. Memory is a design surface, not a database concern.",
        },
      ],
      decisionsShowBeforeAfter: false,
      decisionsEyebrow: "— The build",
      decisionsTitle: "Six decisions that turned a CRM into a system.",
      decisionsSubhead:
        "I skipped 'what screens do we need?' entirely. Each decision below shows what shipped, why, what I rejected, and the signal it was designed to move.",
      decisions: [
        {
          id: "D—01",
          section: "System architecture",
          title: "Start with the objects and their relationships, not the screens",
          after:
            "The mental model was fixed before a screen was drawn: Lead at the centre, with Applications (one per lender), Documents, Tasks, Notes, Assignments and Notifications as related objects, each with its own lifecycle and permission surface. Every later layout decision resolved against that graph.",
          why:
            "One lead could carry multiple loan applications with different lenders, each with its own status and timeline. Designing screens first would have baked that flattening back in. Modelling the graph first meant permissions, notifications and automation could all hang off the same spine.",
          rejected:
            "'What pages do we need?' was the mode the old CRM was built in. Every page owned its own local copy of state and its own private idea of what a lead was, which is exactly the reasoning debt we were rebuilding out of.",
          signal:
            "How often agents ask 'which application are you looking at?' in stand-up. A working object model makes that question rare.",
        },
        {
          id: "D—02",
          section: "Roles & permissions",
          title: "Same data, different responsibility: permissions became architecture",
          after:
            "UC agents and lenders share the same underlying lead and application objects but see different capabilities, different fields, and different actions. Assignment, notes, task-tagging and lender-forwarding all resolve against a permission layer that lives underneath the UI, not inside it.",
          why:
            "The two sides weren't different personas; they were different responsibilities on the same object. Building that into the architecture meant the interface could stay one product instead of two, and future workflows could ship without re-forking screens.",
          rejected:
            "Two parallel CRMs: one for UC agents, one for lenders. Cheaper up front, but every shared workflow (assignment, status change, tagging) would then need to be built and reconciled twice.",
          signal:
            "How rarely a role needs a bespoke screen. If the same object serves both sides, most 'lender-only' asks turn into permission changes, not new pages.",
        },
        {
          id: "D—03",
          section: "Application states",
          title: "Status became a system, not a label",
          after:
            "Each of the 12 application states carries its own required fields, available actions, required communication and downstream events. Automation sits on top of that model: for example, Awaiting Docs → Ready to Login flips automatically once the configured document threshold is met.",
          why:
            "Loan-status changes weren't 'move a chip' events; they were the trigger for real-world work: a Sanctioned row needs sanction date, amount, ROI and tenure; Disbursed needs amount and date; Rejected needs a reason. Treating status as a system made those requirements a rule, not an operator's memory.",
          rejected:
            "A generic 'edit status' modal with the same fields regardless of state. Ships fast, guarantees dirty data, which in a lending workflow means real disputes downstream.",
          signal:
            "% of status transitions that ship with the state's required fields complete on first save. Rising cleanly = the state model is doing its job.",
        },
        {
          id: "D—04",
          section: "Lead Details",
          title: "The Lead Details page as a control centre, not a form",
          after:
            "One page structured around four operator questions (What is happening? What needs my attention? Who is responsible? What can I do next?) with profile, applications (stacked per lender, sorted by progress), documents, tasks, notes and timeline sharing the same operational context. No cross-screen tab-hunting.",
          why:
            "Agents were spending real minutes per lead reconstructing context across disconnected pages. The Lead Details page had to answer their working questions in the order they'd ask them, not in the order the database happens to store them.",
          rejected:
            "A tabbed lead page: one tab per data type. Familiar pattern; punishes the highest-frequency task, which is holding multiple facets in view at once.",
          signal:
            "Time-to-first-action on a newly-assigned lead. If the control centre is doing its job, an agent acts in seconds, not minutes.",
        },
        {
          id: "D—05",
          section: "Timeline",
          title: "The timeline as the system's memory, not an activity feed",
          after:
            "A per-lead event log unifying calls, emails, WhatsApp, status changes, assignments, lender-forwards, application-status transitions, notes and tasks: one shared context layer between UC and lender teams. Reassignments don't erase history; they enrich it.",
          why:
            "Ownership shifts are the highest-risk moments in a 65-day journey. If the incoming person can't reconstruct the last month of work in a scan, the lead re-starts. The timeline had to hold that memory outside anyone's head.",
          rejected:
            "A chronological 'activity feed' that only shows CRM-native events. Excludes the exact events (email, WhatsApp, external calls) where most of the actual work lives.",
          signal:
            "How often reassigned leads regress on their previous status. Falls when the timeline is trusted; rises when people work around it.",
        },
        {
          id: "D—06",
          section: "Notifications",
          title: "Notifications as an event system, not one-off messages",
          after:
            "One event pipeline (note-tag, status change, task change, assignment) resolves into channels (CRM, Slack, email, WhatsApp) by rule, not by hard-coded feature. Adding a new workflow later means registering an event, not shipping a new notification screen.",
          why:
            "Every one-off notification is a future rewrite. Building an event → channel graph early let the notification layer scale with the product instead of being retrofitted every time a new state or role landed.",
          rejected:
            "Feature-scoped notifications wired inline, like 'when a lender does X, ping Slack', a familiar shortcut that guarantees a rewrite the second workflow asks for the same routing rules.",
          signal:
            "New workflow → new event registration only. If a new lender-side feature has to touch the notification UI, the abstraction is leaking.",
        },
      ],
      howIWorkedEyebrow: "— How I worked",
      howIWorked: [
        {
          title: "Made the object model the shared language across teams",
          body: "Before pixels, the PM, engineering leads, ops leads and I aligned on the object graph (Lead, Application, Documents, Tasks, Notes, Assignments, Notifications) and who could act on what. That single artefact stopped 'what should this screen do?' debates and started 'what does this event mean in the system?' ones.",
        },
        {
          title: "Turned status into a spec, together with engineering",
          body: "For each of the 12 application states, I paired with engineering to define required fields, available actions, downstream events and communication triggers as one table. It became the contract between design, backend and QA, and killed the loop where states quietly acquired UI-only rules the API didn't enforce.",
        },
        {
          title: "Ran ops shadowing to earn the permission model",
          body: "The lender vs. UC-agent split was the hottest debate in the room. I ran two afternoons of shadowing on both sides, then walked leadership through the friction moments verbatim. The permission architecture landed because the room had watched an agent lose fifteen minutes to a note they weren't allowed to see, not because I'd argued it well in slides.",
        },
      ],
      outcomesEyebrow: "— What the build established",
      outcomesHeading: { pre: "What the system now ", accent: "supports", post: "." },
      outcomesIntro:
        "A shipped enterprise CRM with adoption-shaped success signals. This case study is about the reasoning and craft: the measured impact lives inside UniCreds' ops dashboards.",
      outcomes: [
        {
          metric: "1 CRM",
          label: "One product, two roles: permissions live in the architecture, not inside the UI",
        },
        {
          metric: "12 states",
          label: "Each with its own fields, actions and downstream events: automation sits on top",
        },
        {
          metric: "1 memory",
          label: "The timeline unifies calls, emails, WhatsApp, statuses, notes and tasks across teams",
        },
      ],
      reflection:
        "Enterprise UX is less about designing screens and more about designing the system that makes those screens make sense. Once I started looking at the relationships between people, permissions, states, data and actions as one system, the interface decisions became the easy part.",
      learnings: [
        "Start with the objects, not the pages. Every screen is a slice of an object graph; if the graph is wrong, no amount of polish makes the pages coherent.",
        "Permissions are a UX surface. What someone cannot see or do is as much a design decision as what they can, and letting engineering own it silently is the fastest way to a confusing product.",
        "State is a system. If a status change requires new fields, new actions or new events, the status is a spec, not a label. Treat it like one from day one.",
      ],
      gallery: [],
    },
  },
];

export const aboutSpans = [
  { text: "I'm a Lead Product Designer in Mumbai, five years into trying to understand " },
  { text: "why people do what they do", accent: true },
  { text: ", and then building things that fit more honestly into that. I get uncomfortable when a product technically works but still " },
  { text: "feels wrong", accent: true },
  { text: ", and I can't leave it alone until I've figured out why. Lately, I've been most drawn to AI, not as a tool, but as a " },
  { text: "new interaction surface", accent: true },
  { text: ". Designing for conversations, for uncertainty, for " },
  { text: "trust", accent: true },
  { text: ", that's where the interesting problems are right now." },
];

export const timeline = [
  { year: "2024", ongoing: true, note: "Lead UI/UX Designer at ASL, driving design across Uniacco, Unischolars, Unicreds & Uniessential" },
  { year: "2023", note: "UI/UX Designer at Unischolars" },
  { year: "2022", note: "Product Designer at Medikabazaar" },
  { year: "2021", note: "Associate Product Designer at CarTrade Tech, where it began" },
];

export const testimonials = [
  {
    id: 1,
    quote:
      "Shakti has a rare mix of empathy and precision. She owned design across our website, lead forms, and internal CRM tools, and it always came from really getting how the user would experience it. Her aesthetic instincts are clean yet comforting, her attention to detail is excellent, and the uplift in KPIs speak for themselves.",
    author: "VP Product & Design",
    role: "Naveen C.",
    bg: "#EFF0F4",
  },
  {
    id: 2,
    quote:
      "She's been great to work with. She's very driven, both by the numbers and by getting things done, and is always willing to put in the extra effort when needed. What I've really appreciated is that she's equally good at working with the team, taking ownership, and helping everyone move things forward.",
    author: "Senior Product Manager",
    role: "Parag M",
    bg: "#F7F7FA",
  },
  {
    id: 3,
    quote:
      "She asks the question everyone else in the room quietly wanted to. Then she ships something that answers it.",
    author: "Founder",
    role: "Health-tech collaboration",
    bg: "#FFFFFF",
  },
];
